/// <amd-module name="Wit/Search/SearchProvider" />
import React = require("react");
import { ITfsPageService } from "Tfs/Platform/Page";
import { Services } from "VSS/Platform/Context";
import { ISearchInput, ISearchQuery } from "VSS/Platform/Search";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { WorkitemSearchHelpComponent } from "./SearchHelp";
import { InstantSearchSuggestionsComponent } from "InstantSearch/Shared/SearchSuggestions";
import { IInvokedProvidersManager } from "InstantSearch/Shared/Services/InvokedProvidersManager";
import { TelemetryWriter } from "InstantSearch/Shared/TelemetryWriter";
import { getQueryTextWithWildCardChar } from "InstantSearch/Shared/Utils";
import { IWITSearchQuery } from "Wit/Search/Types";
import { WorkitemSearchBaseProvider } from "Wit/Search/BaseProvider";
import { SearchWarmer } from "Wit/Search/SearchWarmer";
import { FeatureFlags, InfiniteScrollTakeValue, CustomerIntelligenceConstants, HubName } from "Wit/Search/Constants";

class WorkitemSearchProvider extends WorkitemSearchBaseProvider {
    private _workItemSearchSuggestionsComponentRef = React.createRef<InstantSearchSuggestionsComponent>();
    private _telemetryWriter: TelemetryWriter;
    private _searchWarmer: SearchWarmer;
    private _searchWarmerInitialized = false;

    public getQuery(searchQuery: ISearchQuery): IWITSearchQuery {
        const witSearchQuery: IWITSearchQuery = Object.assign({
            type: "workitem",
            filters: {}
        }, searchQuery);

        // Determine which projects we should be searching.
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            witSearchQuery.filters.Projects = [tfsPageData.project.name];
        }

        if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.InfiniteScroll, false)) {
            witSearchQuery.pageSize = InfiniteScrollTakeValue;
        }

        if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.DisableAggregations, false)) {
            witSearchQuery.includeFacets = false;
        }

        return witSearchQuery;
    }

    public help(searchInput: ISearchInput): JSX.Element {
        if (!this._searchWarmerInitialized) {
            this.searchWarmer.initSearchWarmers();
            this._searchWarmerInitialized = true;
        }

        const showSearchSuggestions = isFeatureEnabled(this.pageContext, "ms.vss-tfs-web.enable-search-suggestions", false);
        if (showSearchSuggestions) {

            return (
                <InstantSearchSuggestionsComponent
                    ref={this._workItemSearchSuggestionsComponentRef}
                    hubName={HubName}
                    featureName={CustomerIntelligenceConstants.Feature}
                    searchInput={searchInput} />
            );
        }
        else {
            return (
                <WorkitemSearchHelpComponent searchInput={searchInput} />
            );
        }
    }

    public performAction(searchQuery: ISearchQuery): Promise<boolean> {
        return super.performAction(searchQuery).then((isActionPerformed) => {
            const showSearchSuggestions = isFeatureEnabled(this.pageContext, "ms.vss-tfs-web.enable-search-suggestions", false);
            if (!isActionPerformed && showSearchSuggestions) {
                const invokedProvidersManager = this.pageContext.getService<IInvokedProvidersManager>("IInvokedProvidersManager");
                if (!invokedProvidersManager.shouldAllowImmersiveSearch()) {
                    // Block all quick navigation scenarios except those 
                    // which are supported by search portal.
                    return true;
                }

                const workItemSuggestionsComponent = this._workItemSearchSuggestionsComponentRef.current;
                if (workItemSuggestionsComponent && !searchQuery.openInNewWindow) {
                    workItemSuggestionsComponent.invokeActionOfSelectedItem();
                    return true;
                } else {
                    // Execute search query with wild card char and land on search portal
                    searchQuery.queryText = getQueryTextWithWildCardChar(searchQuery.queryText);
                    this.telemetryWriter.publish("ImmersiveSearchPerformed");
                }
            }

            return isActionPerformed;
        });
    }

    private get telemetryWriter(): TelemetryWriter {
        if (!this._telemetryWriter) {
            this._telemetryWriter = new TelemetryWriter(this.pageContext, CustomerIntelligenceConstants.Feature);
        }

        return this._telemetryWriter;
    }

    private get searchWarmer(): SearchWarmer {
        if (!this._searchWarmer) {
            this._searchWarmer = new SearchWarmer(this.pageContext);
        }

        return this._searchWarmer;
    }
}

Services.add("workitem-search-provider", { serviceFactory: WorkitemSearchProvider });
